/**
 * @ken/module-products — frontend entry point.
 *
 * Exports exactly what the cockpit shell expects per KEN_LAYER_7 §6.3:
 *   - createRoutes(parent) — factory returning the module's TanStack Router
 *                            children, mounted under the cockpit's tenant
 *                            parent route at boot.
 *
 * The manifest is NOT exported here (CV-19). modules/products/manifest.yaml
 * is the only copy; the build reads it and hands the cockpit the parsed
 * object through the generated registry.
 *
 * 2026-06-15 (story_page_go_wide): the in-list detail Sheet is RETIRED for
 * products in favour of the full-page STORY route (ADR-0081/0082, PARTY
 * archetype — a master/catalog record with a CATALOG stat strip). Clicking a
 * product row navigates to `products/$id/overview`; the parent `products/$id`
 * renders the StoryPageDetail shell + routed tab children (overview / related
 * / notes) with an index redirect to overview. Mirrors the customers story
 * rollout.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { createRoute, redirect } from "@tanstack/react-router";

import type { ProductBrandsSearch } from "./routes/brands";
import type { ProductFilingSearch } from "./routes/filing";
import type { ProductStorySearch } from "./routes/story/product-story-detail";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadProductStoryTabs = () => import("./routes/story/product-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const ProductCreatePage = lazyScreen(
  () => import("./routes/product-create"),
  "ProductCreatePage",
);
// product_filing slice 1: "Products waiting to be filed" (docs/plans/product_filing.md §5).
const ProductFilingPage = lazyScreen(
  () => import("./routes/filing"),
  "ProductFilingPage",
);
// product_filing slices 2-3: the Brands page (brands and name rules, PRD 54).
const ProductBrandsPage = lazyScreen(
  () => import("./routes/brands"),
  "ProductBrandsPage",
);
const ProductListPage = lazyScreen(
  () => import("./routes/product-list"),
  "ProductListPage",
);
const ProductStoryDetail = lazyScreen(
  () => import("./routes/story/product-story-detail"),
  "ProductStoryDetail",
);
const ProductActivityRoute = lazyScreen(
  loadProductStoryTabs,
  "ProductActivityRoute",
);
const ProductNotesRoute = lazyScreen(
  loadProductStoryTabs,
  "ProductNotesRoute",
);
const ProductCustomerViewRoute = lazyScreen(
  loadProductStoryTabs,
  "ProductCustomerViewRoute",
);
const ProductMovementsRoute = lazyScreen(
  loadProductStoryTabs,
  "ProductMovementsRoute",
);
const ProductOverviewRoute = lazyScreen(
  loadProductStoryTabs,
  "ProductOverviewRoute",
);
const ProductPriceTiersRoute = lazyScreen(
  loadProductStoryTabs,
  "ProductPriceTiersRoute",
);
const ProductRelatedRoute = lazyScreen(
  loadProductStoryTabs,
  "ProductRelatedRoute",
);

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "products",
    // The list's filter/search/highlight are URL-borne so back-navigation
    // from the story page restores them (ADR-0081 §3). validateSearch keeps
    // them typed + reload-proof.
    validateSearch: (raw: Record<string, unknown>): ProductListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // The Grouped (tree) ↔ Flat view toggle is URL-borne so it survives
      // reload + back, consistent with the other story-window params above.
      // "flat" is the only non-default value; absent ⇒ the grouped tree.
      ...(raw["view"] === "flat" ? { view: "flat" } : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
      // product_filing: a Filing card's "See in the Products list" link shows
      // exactly the products under one QuickBooks header (its gid).
      ...(typeof raw["qb_header"] === "string" && raw["qb_header"]
        ? { qb_header: raw["qb_header"] }
        : {}),
    }),
    component: ProductListPage,
  });

  // Declared before products/$id so "filing" is never read as a product id.
  const filingRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "products/filing",
    validateSearch: (raw: Record<string, unknown>): ProductFilingSearch => ({
      ...(raw["tab"] === "unsorted" ? { tab: "unsorted" as const } : {}),
      ...(raw["need"] === "brand" || raw["need"] === "category"
        ? { need: raw["need"] }
        : {}),
      ...(raw["inactive"] === "1" || raw["inactive"] === 1 || raw["inactive"] === true
        ? { inactive: "1" }
        : raw["inactive"] === "0" || raw["inactive"] === 0 || raw["inactive"] === false
          ? { inactive: "0" }
          : {}),
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["group"] === "string" && raw["group"]
        ? { group: raw["group"] }
        : {}),
    }),
    component: ProductFilingPage,
  });

  // Also before products/$id so "brands" is never read as a product id.
  const brandsRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "products/brands",
    validateSearch: (raw: Record<string, unknown>): ProductBrandsSearch => ({
      ...(raw["tab"] === "rules" ? { tab: "rules" as const } : {}),
    }),
    component: ProductBrandsPage,
  });

  const createRouteEntry = createRoute({
    getParentRoute: () => parentRoute,
    path: "products/new",
    component: ProductCreatePage,
  });

  // Units of measure and price levels moved to Rates and units (ADR-0276,
  // SS-B5-T1); that module also redirects the old /products/... addresses.

  // ── Story-page detail (ADR-0081): parent products/$id shell + routed
  //    tab children + an index redirect to …/overview. ────────────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "products/$id",
    // The list's filter/search + the originating row id ride into the detail
    // route so Prev/Next walks the SAME filtered set and back restores state.
    // `view` rides along too so the in-page back link can hand the Grouped↔Flat
    // mode back to the catalog (owner QA report 2026-08-04).
    validateSearch: (raw: Record<string, unknown>): ProductStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? { filter_status: raw["filter_status"] }
        : {}),
      ...(typeof raw["from"] === "string" && raw["from"]
        ? { from: raw["from"] }
        : {}),
      ...(raw["view"] === "flat" ? { view: "flat" as const } : {}),
    }),
    component: ProductStoryDetail,
  });

  // Index redirect products/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/products/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: ProductOverviewRoute,
  });
  const customerViewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "customer-view",
    component: ProductCustomerViewRoute,
  });
  // T208. The tab body has existed since the price-levels fold-in and was
  // imported by nothing; the endpoint behind it has existed just as long.
  // Declaring the route child is half the mount — the tab item in
  // product-story-detail.tsx is the other half, and the Activity tab shipped
  // for months with only one of the two.
  const priceTiersRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "price-tiers",
    component: ProductPriceTiersRoute,
  });
  // SMV-E — the product's stock movements. The tab LINK is gated on
  // `inventory_management.view` in the shell; the ROUTE exists either way so a
  // deep link renders the denied panel rather than a blank page.
  const movementsRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "movements",
    component: ProductMovementsRoute,
  });
  const relatedRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "related",
    component: ProductRelatedRoute,
  });
  const notesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "notes",
    component: ProductNotesRoute,
  });
  // The Activity tab link has shipped since the story rollout; its route child
  // never did, so the panel rendered blank no matter what the timeline held
  // (ADR-0122 slice C / owner QA report f378ecaa).
  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: ProductActivityRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [
      detailIndexRoute,
      overviewRoute,
      movementsRoute,
      customerViewRoute,
      priceTiersRoute,
      relatedRoute,
      notesRoute,
      activityRoute,
    ],
  );

  return [
    listRoute,
    createRouteEntry,
    filingRoute,
    brandsRoute,
    detailRoute,
  ];
};

/** List search params (filter/search/highlight/view) — URL-borne for restore.
 *  `view` is "flat" for the flat table; absent (the default) is the grouped
 *  tree. */
/** POP-01: a list whose only search parameter is the record open in its pop-up. */
export interface RecordPopupSearch {
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
}

export interface ProductListSearch {
  q?: string;
  filter_status?: string;
  highlight?: string;
  view?: "flat";
  /** POP-01: the record open in the pop-up over the list. */
  open?: string;
  /** POP-02: the pop-up's open tab (absent: Overview). */
  tab?: string;
  /** product_filing: only the products under this QuickBooks top header (its gid). */
  qb_header?: string;
}
